import {forwardRef, useImperativeHandle} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {
  Button,
  ProgressRing,
  ResponsiveSheet,
  useCountdown,
  useModalControls,
} from '@chekinapp/ui';
import {SheetActions} from 'components/common/SheetActions';
import {closeSession} from 'components/sessions/utils';

export type SessionCloseModalRef = {
  open: () => void;
};

type Props = {
  reservationId: string;
  initialTime?: number;
  onExpire?: () => void;
};

const DEFAULT_COUNTDOWN = 60;

const SessionCloseModal = forwardRef<SessionCloseModalRef, Props>(
  ({reservationId, initialTime = DEFAULT_COUNTDOWN, onExpire}, ref) => {
    const {t} = useTranslation();
    const {isOpen, closeModal, openModal} = useModalControls();
    const endSession = onExpire ?? (() => closeSession(reservationId));

    const {timeLeft} = useCountdown({
      initialTime,
      onFinish: endSession,
      enabled: isOpen,
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          open: openModal,
        };
      },
      [openModal],
    );

    return (
      <ResponsiveSheet
        open={isOpen}
        onClose={closeModal}
        title={t('session_close_modal.title')}
        closeOnOverlayClick={false}
        className="session-close-modal"
        dialogClassName="!max-w-[460px]"
        contentClassName="px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-8"
      >
        <div className="flex flex-col gap-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <ProgressRing
              value={(timeLeft / initialTime) * 100}
              size={72}
              strokeWidth={5}
              halo
              label={timeLeft}
              labelClassName="text-xl tabular-nums"
              transitionMs={1000}
            />
            <p className="text-balance text-sm text-[var(--chekin-text-secondary)]">
              <Trans i18nKey="session_close_modal.text" values={{countdown: timeLeft}} />
            </p>
          </div>
          <SheetActions>
            <Button onClick={closeModal}>{t('continue_session')}</Button>
            <Button variant="link" onClick={endSession}>
              {t('end_session_now')}
            </Button>
          </SheetActions>
        </div>
      </ResponsiveSheet>
    );
  },
);

export {SessionCloseModal};
